import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Grid,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    styled,
} from '@mui/material';

import {
    CloudDownload,
    CloudUpload,
    LightbulbOutlined,
    Google,
} from '@mui/icons-material';

import {
    DatePicker,
} from '@mui/x-date-pickers';

import dayjs from 'dayjs';

import {
    useGoogleLogin,
} from '@react-oauth/google';

import copyToClipboard from './copyToClipboard.js';

import React from 'react';

function App({ innovation_email }) {
    const api_endpoint = document.location.hostname === 'localhost' ? 'http://localhost:8080' : 'https://geo-reports.mobilefuse.com';

    const [step, setStep]                         = React.useState(0);
    const [google_token, setGoogleToken]          = React.useState(localStorage.getItem('google_token') ?? null);
    const [auth_loading, setAuthLoading]          = React.useState(false);
    const [date_range_start, setDateRangeStart]   = React.useState('2024-12-01');
    const [date_range_end, setDateRangeEnd]       = React.useState('2024-12-30');
    const [omn, setOmn]                           = React.useState('johnson_johnson_december_2023_april_2024_109495');
    const [report_split_mode, setReportSplitMode] = React.useState(true);
    const [merge_reports, setMergeReports]        = React.useState(false);
    const [events_loading, setEventsLoading]      = React.useState(false);
    const [events_data, setEventsData]            = React.useState(null);
    const [poi_file_metadata, setPoiFileMetadata] = React.useState(null);
    const [poi_file_contents, setPoiFileContents] = React.useState(null);
    const [poi_file_headers, setPoiFileHeaders]   = React.useState(null);
    const [poi_data, setPoiData]                  = React.useState(null);
    const [poi_uploading, setPoiUploading]        = React.useState(false);
    const [lat_column, setLatColumn]              = React.useState(null);
    const [lon_column, setLonColumn]              = React.useState(null);
    const [zip_column, setZipColumn]              = React.useState(null);
    const [processing, setProcessing]             = React.useState(false);
    const [processing_data, setProcessingData]    = React.useState(null);
    const [error_details, setErrorDetails]        = React.useState(null);

    const google_login = useGoogleLogin({
        'onSuccess': (response) => onAuthTest(response.access_token),
        'onError': (error) => {
            console.error(error);
        },
        'hosted_domain': 'mobilefuse.com',
        'prompt': '',
    });

    async function onAuthTest(access_token) {
        setAuthLoading(true);

        const response = await fetch(`${api_endpoint}/ping`, {
            'headers': {
                'Authorization': `Bearer ${access_token}`,
            },
        });
        const status = response.status;

        if (status !== 200) {
            localStorage.removeItem('google_token');

            setAuthLoading(false);
            setGoogleToken(null);
            return;
        }

        localStorage.setItem('google_token', access_token);

        setStep(1);
        setAuthLoading(false);
        setGoogleToken(access_token);
    }

    async function fetchEvents(date_range_start, date_range_end, omn, cmn) {
        const uri      = `fetch-events?date_range_start=${date_range_start}&date_range_end=${date_range_end}&omn=${omn}` + (cmn ? `&cmn=${cmn}` : '');
        const response = await fetch(`${api_endpoint}/${uri}`, {
            'headers': {
                'Authorization': `Bearer ${google_token}`,
            },
        });

        if (response?.status !== 200) {
            throw `/fetch-events has failed with status: ${response.status}\nuri: ${uri}`;
        }

        let data = null;

        try {
            data = await response.json();
        } catch (exception) {
            throw `Couldn't parse /fetch-events response.\nexception: ${exception.message}`;
        }

        if (data['data_preview'].length > 10) {
            data['data_preview'].length = 10;
        }

        return data;
    }

    async function process(s3_keys_events, file_prefix, cmns) {
        const body = JSON.stringify({
            's3_keys_events': s3_keys_events,
            'cmns': cmns ?? [],
            's3_key_pois': poi_data['s3_key'],
            'lat_column': lat_column,
            'lon_column': lon_column,
            'zip_column': zip_column,
            'poi_headers': poi_file_headers,
            'file_prefix': file_prefix,
        });

        const response = await fetch(`${api_endpoint}/process`, {
            'method': 'post',
            'headers': {
                'Authorization': `Bearer ${google_token}`,
                'Content-Type': 'application/json',
            },
            'body': body,
        });

        if (response?.status !== 200) {
            throw `/process has failed with status: ${response.status}\nbody: ${body}`;
        }

        let data = null;

        try {
            data = await response.json();
        } catch (exception) {
            throw `Couldn't parse /process response.\nexception: ${exception.message}`;
        }

        return data;
    }

    async function onPullEvents() {
        setEventsLoading(true);

        // Single report:
        if (!report_split_mode) {
            let events = null;

            try {
                events = await fetchEvents(date_range_start, date_range_end, omn);
            } catch (exception) {
                setErrorDetails(exception);
                return;
            }

            setEventsData([{
                'cmn': null,
                'events': events,
            }]);
            setEventsLoading(false);
            setStep(2);

            return;
        }

        // Multiple reports per CMN:

        let cmns = await fetch(`${api_endpoint}/fetch-creatives?omn=${omn}`, {
            'headers': {
                'Authorization': `Bearer ${google_token}`,
            },
        });

        if (cmns?.status !== 200) {
            setErrorDetails(`/fetch-creatives has failed with status: ${cmns.status}\nomn: ${omn}`);
            return;
        }

        try {
            cmns = await cmns.json();
        } catch (exception) {
            setErrorDetails(`Couldn't parse /fetch-creatives response.\nexception: ${exception.message}`);
            return;
        }

        const events_data = [];
        const promises    = [];

        for (const cmn of cmns) {
            promises.push(fetchEvents(date_range_start, date_range_end, omn, cmn));
        }

        let data = null;

        try {
            data = await Promise.all(promises);
        } catch (exception) {
            setErrorDetails(exception);
            return;
        }

        for (let i = 0; i < data.length; i++) {
            const cmn    = cmns[i];
            const events = data[i];

            events_data.push({
                'cmn': cmn,
                'events': events,
            });
        }

        events_data.sort((a, b) => b['events']['rows'] - a['events']['rows']);

        setEventsData(events_data);
        setEventsLoading(false);
        setStep(2);
    }

    async function onPoiFileChange(e) {
        const file = e.target.files.item(0);
        setPoiFileMetadata(file);

        const file_contents = await file.text();
        setPoiFileContents(file_contents);

        const headers = file_contents.split(/\r?\n/)[0].split(',');
        setPoiFileHeaders(headers);

        for (const header of headers) {
            const lc = header.toLowerCase();

            if (lc.includes('lat') && !lat_column) {
                setLatColumn(header);
                continue;
            }

            if (lc.includes('lon') && !lon_column) {
                setLonColumn(header);
                continue;
            }

            if (lc.includes('zip') && !zip_column) {
                setZipColumn(header);
                continue;
            }
        }
    }

    async function uploadPoiDatabase() {
        setPoiUploading(true);

        let response = await fetch(`${api_endpoint}/upload-poi-database`, {
            'method': 'post',
            'headers': {
                'Authorization': `Bearer ${google_token}`,
                'Content-Type': 'text/csv',
            },
            'body': poi_file_contents,
        });

        if (response?.status !== 200) {
            setErrorDetails(`/upload-poi-database has failed with status: ${response.status}\n`);
            return;
        }

        try {
            response = await response.json();
        } catch (exception) {
            setErrorDetails(`Couldn't parse /upload-poi-database response.\nexception: ${exception.message}`);
            return;
        }

        setPoiUploading(false);
        setPoiData(response);
        setStep(4);
    }

    const VisuallyHiddenInput = styled('input')({
        'clip': 'rect(0 0 0 0)',
        'clipPath': 'inset(50%)',
        'height': 1,
        'overflow': 'hidden',
        'position': 'absolute',
        'bottom': 0,
        'left': 0,
        'whiteSpace': 'nowrap',
        'width': 1,
    });

    React.useLayoutEffect(() => {
        // Try to sign-in automatically with the saved Google access token:

        if (step !== 0 || !google_token || auth_loading) {
            return;
        }

        onAuthTest(google_token);
    }, [step]);

    React.useEffect(() => {
        if (step !== 5) {
            return;
        }

        // Start processing:
        setProcessing(true);

        (async() => {
            let result = null;

            // Single non-merged report to generate (all CMNs mixed):
            if (events_data.length == 1 && !merge_reports) {
                try {
                    result = await process([events_data[0]['events']['s3_key']], omn);
                } catch (exception) {
                    setErrorDetails(exception);
                    return;
                }

                setProcessingData([{
                    'cmn': null,
                    'result': result,
                }]);

                setProcessing(false);
                setStep(6);

                return;
            }

            // Create multiple reports per cmn and merge into one report:
            if (merge_reports) {
                const cmn_data = events_data.filter((data) => data['events']['s3_key'] !== null);
                const s3_keys  = cmn_data.map((data) => data['events']['s3_key']);
                const cmns     = cmn_data.map((data) => data['cmn']);

                try {
                    result = await process(s3_keys, omn, cmns);
                } catch (exception) {
                    setErrorDetails(exception);
                    return;
                }

                setProcessingData([{
                    'cmn': null,
                    'result': result,
                }]);

                setProcessing(false);
                setStep(6);

                return;
            }

            // Create multiple reports per cmn (without merging):
            const promises = [];
            let data = null;

            for (const row of events_data) {
                if (row['events']['rows'] <= 1) {
                    promises.push(Promise.resolve(null));
                    continue;
                }

                promises.push(process([row['events']['s3_key']], omn, [row['cmn']]));
            }

            try {
                data = await Promise.all(promises);
            } catch (exception) {
                setErrorDetails(exception);
                return;
            }

            const processing_data = [];

            for (let i = 0; i < data.length; i++) {
                const cmn    = events_data[i]['cmn'];
                const result = data[i];

                processing_data.push({
                    'cmn': cmn,
                    // Can be null:
                    'result': result,
                });
            }

            setProcessingData(processing_data);
            setProcessing(false);
            setStep(6);
        })();
    }, [step, events_data]);

    return <Paper sx={{
        'padding': 2,
        'minWidth': '560px',
        'maxWidth': '560px',
        'justifyContent': 'center',
        'alignItems': 'center',
        'mx': 'auto',
    }} elevation={ 0 }><Grid container rowGap={ 2 } justifyContent='center'>
        { error_details && <Alert
            severity='error'
        >
            Looks like we encountered an issue while processing your request:
            <br/><br/>
            <textarea
                readOnly
                style={{
                    'resize': 'none',
                    'width': '95%',
                    'height': '64px',
                }}
                value={ error_details }
            ></textarea>
            <br/><br/>
            Please copy the details above and reach out to <a href={ `mailto:${innovation_email}` }>Innovation</a> team.

            <div style={{
                'textAlign': 'center',
                'marginTop': '16px',
            }}>
                <Button
                    variant='text'
                    onClick={ () => copyToClipboard(error_details) }
                    size='small'
                >
                    Copy to clipboard
                </Button>
            </div>
        </Alert> }

        { step === 0 && !google_token && <React.Fragment>
            <Grid container sx={{
                'justifyContent': 'center',
                'px': 2,
                'gap': 2,
            }}>
                <Grid item xs={ 12 }>
                    <Typography variant='h6' sx={{
                        'textAlign': 'center',
                    }}>
                        Please login using your MobileFuse Google account:
                    </Typography>
                </Grid>

                <Grid item xs={ 6 } display='flex' justifyContent='center' textAlign='center'>
                    { !auth_loading ? <Button
                        variant='outlined'
                        color='success'
                        disabled={ auth_loading }
                        onClick={ () => google_login() }
                    >
                        Sign in With Google&nbsp;&nbsp;<Google/>
                    </Button> : <CircularProgress /> }
                </Grid>
            </Grid>
        </React.Fragment> }

        { step === 1 && <React.Fragment>
            <Typography variant='h5'>
                Step 1: Select events
            </Typography>

            <Grid item xs={ 12 }>
                <Typography variant='h6' mb={ 1.5 }>
                    Select date range:
                </Typography>

                <Grid container sx={{
                    'justifyContent': 'center',
                    'columnGap': 2,
                }}>
                    <Grid item xs={ 5 }>
                        <DatePicker
                            label='From'
                            value={ dayjs(date_range_start).format('MM/DD/YYYY') }
                            onChange={ (value) => setDateRangeStart(dayjs(value).format('YYYY-MM-DD')) }
                            disabled={ events_loading }
                            renderInput={ (params) => <TextField { ... params } /> }
                        />
                    </Grid>

                    <Grid item xs={ 5 }>
                        <DatePicker
                            label='To (inclusive)'
                            value={ dayjs(date_range_end).format('MM/DD/YYYY') }
                            onChange={ (value) =>setDateRangeEnd(dayjs(value).format('YYYY-MM-DD')) }
                            disabled={ events_loading }
                            renderInput={ (params) => <TextField { ... params } /> }
                        />
                    </Grid>

                    <Grid item xs={ 12 }>
                        <Typography variant='subtitle1' mt={ 1.5 }>
                            Your selected date range is:
                            <br/>

                            <b>From</b>&nbsp;
                            { dayjs(date_range_start).format('YYYY-MM-DD') } <b>UTC</b>
                            &nbsp;

                            <br/>

                            <b>To</b>&nbsp;
                            { dayjs(date_range_end).format('YYYY-MM-DD') } <b>UTC</b> <b>(inclusive)</b>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={ 12 }>
                <Typography variant='h6' mb={ 1.5 }>
                    Select campaign (Order Machine Name):
                </Typography>

                <Grid container sx={{
                    'justifyContent': 'center',
                    'px': 2,
                }}>
                    <Grid item xs={ 11 }>
                        <TextField
                            variant='outlined'
                            label='omn'
                            fullWidth
                            value={ omn }
                            onChange={ (e) => setOmn(e.target.value) }
                            disabled={ events_loading }
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={ 12 }>
                <Typography variant='h6' mb={ 1.5 }>
                    Output options:
                </Typography>

                <Grid container sx={{
                    'justifyContent': 'center',
                    'px': 2,
                }}>
                    <Grid item xs={ 11 }>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={ report_split_mode }
                                        onChange={ (e) => setReportSplitMode(e.target.checked) }
                                        disabled={ events_loading }
                                    />
                                }
                                label='Generate separate reports for each creative in campaign.'
                            />
                        </FormGroup>
                    </Grid>

                    <Grid item xs={ 11 } pl={ 4 }>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        disabled={ !report_split_mode || events_loading }
                                        checked={ merge_reports }
                                        onChange={ (e) => setMergeReports(e.target.checked) }
                                    />
                                }
                                label='Merge reports into single file.'
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={ 12 } textAlign='center' mt={ 1 }>
                <Button variant='outlined' disabled={ events_loading } onClick={ onPullEvents }>Continue</Button>
            </Grid>

            <Grid item xs={ 12 } display={ events_loading ? 'block' : 'none' }>
                <LinearProgress/>
            </Grid>
        </React.Fragment> }

        { step === 2 && <React.Fragment>
            <Typography variant='h5'>
                Step 2: Preview events
            </Typography>

            <Grid item xs={ 12 }>
                <TableContainer component={ Paper }>
                    <Table size='small'>
                        <TableHead>
                            { events_data.length == 1 && <TableRow>
                                <TableCell>timestamp</TableCell>
                                <TableCell>lat</TableCell>
                                <TableCell>lon</TableCell>
                                <TableCell>event type</TableCell>
                            </TableRow> }

                            { events_data.length > 1 && <TableRow>
                                <TableCell>cmn</TableCell>
                                <TableCell>events</TableCell>
                                <TableCell/>
                            </TableRow> }
                        </TableHead>

                        <TableBody>
                            { events_data.length == 1 && events_data[0]['events']['data_preview'].map((row, i) => <TableRow key={ i }>
                                <TableCell sx={{ 'textAlign': 'right' }}>{ row[0] }</TableCell>
                                <TableCell sx={{ 'textAlign': 'right' }}>{ row[1] }</TableCell>
                                <TableCell sx={{ 'textAlign': 'right' }}>{ row[2] }</TableCell>
                                <TableCell sx={{ 'textAlign': 'right' }}>{ row[3] }</TableCell>
                            </TableRow>) }

                            { events_data.length > 1 && events_data.map((row, i) => <TableRow key={ i }>
                                <TableCell sx={{ 'textAlign': 'left' }}>
                                    <Box width='260px' overflow='auto'>
                                        { row['cmn'].replace(omn + '_', '') }
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ 'textAlign': 'right' }}>{ row['events']['rows'] }</TableCell>
                                <TableCell sx={{ 'textAlign': 'right' }}>
                                    { row['events']['s3_url'] && <Button
                                        variant='outlined'
                                        color='secondary'
                                        size='small'
                                        onClick={ () => window.open(row['events']['s3_url'], '_blank') }
                                    >
                                        <CloudDownload/>&nbsp;Download
                                    </Button> }
                                </TableCell>
                            </TableRow>)}
                        </TableBody>
                    </Table>

                    { events_data.length == 1 && <Grid container alignItems='center'>
                        <Grid item xs={ 6 } sx={{
                            'px': 2,
                            'py': 2,
                        }} textAlign='center'>
                            <Typography variant='body1'>
                                <b>Total rows found:</b> { events_data[0]['events']['rows'] ?? 0 }
                            </Typography>
                        </Grid>

                        <Grid item xs={ 6 } textAlign='center'>
                            <Button
                                variant='outlined'
                                color='secondary'
                                size='small'
                                disabled={ events_data[0]['events']['s3_url'] == null }
                                onClick={ () => window.open(events_data[0]['events']['s3_url'], '_blank') }>
                                    <CloudDownload/>&nbsp;Download All
                            </Button>
                        </Grid>
                    </Grid> }
                </TableContainer>
            </Grid>

            <Grid item xs={ 6 } textAlign='center'>
                <Grid container gap={ 2 }>
                    <Grid item xs={ 5 }>
                        <Button variant='outlined' color='secondary' onClick={ () => setStep(1) }>Go Back</Button>
                    </Grid>

                    <Grid item xs={ 5 }>
                        <Button
                            disabled={ (events_data?.[0]?.['events']?.['s3_url'] ?? null) == null }
                            variant='outlined'
                            onClick={ () => setStep(3) }
                        >
                            Continue
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        </React.Fragment> }

        { step === 3 && <React.Fragment>
            <Typography variant='h5'>
                Step 3: Upload a POI database (.csv)
            </Typography>

            <Grid item xs={ 12 }>
                <Alert color='secondary' variant='outlined' icon={ false }>
                    <Box display='flex' alignItems='center'>
                        <LightbulbOutlined/>&nbsp;&nbsp;&nbsp;Prior to upload, please review the following guidelines:
                    </Box>

                    <ul>
                        <li>You have to use a CSV (Comma-Separated Values) file format.</li>
                        <li>You have to use comma (',') as a delimiter.</li>
                        <li>You have to include a headers line in your file.</li>
                        <li>You have to supply separate columns for latitude, longitude and zip code.</li>
                        <li>You can enclose values in double quotes ("). You have to if the value contains a delimiter (comma).</li>
                    </ul>

                    <Box textAlign='center'>
                        <Button
                            variant='outlined'
                            color='secondary'
                            onClick={ () => window.open('https://pastebin.com/raw/gwESkUAm', '_blank') }
                        >
                            View Example
                        </Button>
                    </Box>
                </Alert>
            </Grid>

            <Grid item xs={ 6 } textAlign='center'>
                <Button
                    component='label'
                    role={ undefined }
                    variant='outlined'
                    tabIndex={ -1 }
                    startIcon={ <CloudUpload/> }
                    disabled={ poi_uploading }
                >
                    Select file
                    <VisuallyHiddenInput type='file' accept='text/csv' onChange={ onPoiFileChange }/>
                </Button>
            </Grid>

            <Grid item xs={ 12 } textAlign='center'>
                <Typography variant='body1'>
                    { poi_file_metadata ? `${poi_file_metadata['name']}, ${poi_file_metadata['size']} b` : 'No file selected.' }
                </Typography>
            </Grid>

            <Grid item xs={ 12 } textAlign='center'>
                <Button
                    variant='outlined'
                    disabled={ !poi_file_metadata || poi_uploading }
                    onClick={ uploadPoiDatabase }>
                        Continue
                </Button>
            </Grid>

            <Grid item xs={ 12 }>
                <LinearProgress sx={{
                    'display': poi_uploading ? 'block' : 'none',
                }}/>
            </Grid>
        </React.Fragment> }

        { step === 4 && <React.Fragment>
            <Typography variant='h5'>
                Step 4: Column mappings
            </Typography>

            <Grid item xs={ 12 }>
                <Grid container justifyContent='center'>
                    <Grid item xs={ 4 }>
                        <Typography variant='h6'>
                            Latitude column:
                        </Typography>
                    </Grid>

                    <Grid item xs={ 4 }>
                        <Typography variant='h6'>
                            Longitude column:
                        </Typography>
                    </Grid>

                    <Grid item xs={ 4 }>
                        <Typography variant='h6'>
                            Zip Code column:
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container justifyContent='center'>
                    <Grid item xs={ 4 } display='flex' justifyContent='center'>
                        <Select size='small' value={ lat_column } label='Latitude column' onChange={ (e) => setLatColumn(e.target.value) }>
                            { poi_file_headers.map((item, i) => <MenuItem key={ i } value={ item }>{ item }</MenuItem>) }
                        </Select>
                    </Grid>

                    <Grid item xs={ 4 } display='flex' justifyContent='center'>
                        <Select size='small' value={ lon_column } label='Longitude column' onChange={ (e) => setLonColumn(e.target.value) }>
                            { poi_file_headers.map((item, i) => <MenuItem key={ i } value={ item }>{ item }</MenuItem>) }
                        </Select>
                    </Grid>

                    <Grid item xs={ 4 } display='flex' justifyContent='center'>
                        <Select size='small' value={ zip_column } label='Zip Code column' onChange={ (e) => setZipColumn(e.target.value) }>
                            { poi_file_headers.map((item, i) => <MenuItem key={ i } value={ item }>{ item }</MenuItem>) }
                        </Select>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={ 12 } textAlign='center' mt={ 1 }>
                <Button variant='outlined' disabled={ !lat_column || !lon_column } onClick={ () => setStep(5) }>Confirm and continue</Button>
            </Grid>
        </React.Fragment> }

        { step === 5 && <React.Fragment>
            <Typography variant='h5'>
                Step 5: Processing
            </Typography>

            <Grid item xs={ 12 }>
                <LinearProgress sx={{
                    'display': processing ? 'block' : 'none',
                }}/>
            </Grid>
        </React.Fragment> }

        { step === 6 && processing_data.length === 1 && <React.Fragment>
            <Typography variant='h5'>
                Step 6: Summary & Download
            </Typography>

            <Grid item xs={ 12 }>
                <Typography variant='body1'>
                    <b>Events:</b> { events_data.map((data) => data['events']['rows']).reduce((sum, val) => sum + val, 0) }
                </Typography>

                <Typography variant='body1'>
                    <b>Time spent:</b> { processing_data[0]['result']['time_elapsed'] } s
                </Typography>
            </Grid>

            <Grid item xs={ 12 } textAlign='center'>
                <Button variant='outlined' onClick={ () => window.open(processing_data[0]['result']['s3_url'], '_blank') }>Download Report</Button>
            </Grid>
        </React.Fragment> }

        { step === 6 && processing_data.length > 1 && <React.Fragment>
            <Typography variant='h5'>
                Step 6: Summary & Download
            </Typography>

            <TableContainer component={ Paper }>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>cmn</TableCell>
                            <TableCell>events</TableCell>
                            <TableCell>time</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        { processing_data.map((report, i) => <TableRow key={ i }>
                            <TableCell>
                                <Box width='180px' overflow='auto'>
                                    { report['cmn'].replace(omn + '_', '') }
                                </Box>
                            </TableCell>

                            <TableCell>{ events_data[i]['events']['rows'] }</TableCell>

                            <TableCell sx={{ 'minWidth': '72px'}}>
                                { report['result'] ? `${report['result']['time_elapsed']} s` : ' - ' }
                            </TableCell>

                            <TableCell>
                                { report['result'] && <Grid container gap={ 1 }>
                                    <Grid item xs={ 12 }>
                                        <Button
                                            variant='outlined'
                                            color='secondary'
                                            size='small'
                                            onClick={ () => window.open(report['result']['s3_url'], '_blank') }
                                        >
                                            <CloudDownload/>&nbsp;Download
                                        </Button>
                                    </Grid>
                                </Grid> }
                            </TableCell>
                        </TableRow>) }
                    </TableBody>
                </Table>
            </TableContainer>
        </React.Fragment> }
    </Grid></Paper>;
}

export default App;
